import Logo from "@adora/shared/components/adora-logo";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Auth from "@adora/shared/utils/auth";
import { mutate } from "swr";
import { useAtom } from "jotai";
import { Box, Flex, Typography, IconButton, Icon, IconType } from "flux";
import { leftNavCollapsedAtom } from "./left-nav-collapsed-atom";

const EXPANDED_WIDTH = 260;
const COLLAPSED_WIDTH = 64;

const initialItems = [
  {
    id: 1,
    name: "Brands",
    shortName: "Br",
    link: "/brands",
    selected: false
  },
  {
    id: 2,
    name: "Batches",
    shortName: "Ba",
    link: "/batches",
    selected: false
  },
  {
    id: 3,
    name: "Users",
    shortName: "Us",
    link: "/users",
    selected: false
  },
  {
    id: 4,
    name: "Flux",
    shortName: "Fx",
    link: "/flux",
    selected: false
  },
  {
    id: 5,
    name: "Tools",
    shortName: "To",
    link: "/tools",
    selected: false
  }
];

const LeftNav: React.FC = () => {
  const [items, setItems] = useState(initialItems);
  const [collapsed, setCollapsed] = useAtom(leftNavCollapsedAtom);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const path = location.pathname;
    const hasSelection = items.some((item) => item.selected);

    if (path === "/" && !hasSelection) {
      items[0].selected = true;
      setItems([...items]);
      navigate(items[0].link);
    } else if (!hasSelection) {
      const trimLeadingSlash = (str: string) => (str.startsWith("/") ? str.slice(1) : str);
      const loc = trimLeadingSlash(path);

      const matchingItem = items.find((item) => loc.startsWith(trimLeadingSlash(item.link)));
      if (matchingItem) {
        matchingItem.selected = true;
        setItems([...items]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleItemClick = (id: number) => {
    const newItems = items.map((item) => {
      if (item.id === id) {
        return { ...item, selected: true };
      }

      return { ...item, selected: false };
    });

    setItems(newItems);
  };

  const onLogoutClicked = () => {
    mutate(() => true, undefined, { revalidate: false });
    Auth.logout(true);
  };

  const toggleCollapsed = () => {
    setCollapsed((prev) => !prev);
  };

  return (
    <Box
      width={collapsed ? `${COLLAPSED_WIDTH}px` : `${EXPANDED_WIDTH}px`}
      bgcolor="rgb(240 249 255)"
      sx={{
        transition: "width 200ms ease-in-out",
        overflow: "hidden",
        flexShrink: 0
      }}
    >
      <Flex width="100%" flexDirection="column" height="100%">
        {/* Header with logo and toggle */}
        <Box padding={collapsed ? "16px 8px" : "24px"}>
          {!collapsed && (
            <Typography variant="subtitle1" color="textSecondary">
              Internal
            </Typography>
          )}
          <Flex
            flexDirection={collapsed ? "column" : "row"}
            alignItems="center"
            justifyContent={collapsed ? "center" : "space-between"}
            gap="8px"
          >
            <Link to="/" style={{ textDecoration: "inherit", color: "inherit", display: "flex", alignItems: "center" }}>
              <Logo width={collapsed ? "32px" : undefined} />
            </Link>
            <IconButton
              onClick={toggleCollapsed}
              dataDogIdentifier="left-nav-collapse-toggle"
              sx={{ width: 32, height: 32 }}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <Icon name={collapsed ? IconType.ChevronRight : IconType.ChevronLeft} width={16} height={16} />
            </IconButton>
          </Flex>
        </Box>

        {/* Nav items */}
        {items.map((item) => (
          <Link
            key={item.id}
            to={item.link}
            onClick={() => handleItemClick(item.id)}
            style={{ textDecoration: "inherit", color: "inherit" }}
            title={collapsed ? item.name : undefined}
          >
            <Flex
              flexDirection="row"
              py="12px"
              px={collapsed ? "0px" : "32px"}
              justifyContent={collapsed ? "center" : "flex-start"}
              sx={{ "&:hover": { bgcolor: "rgb(226 232 240)" } }}
            >
              {collapsed ? (
                <Typography
                  variant="body1"
                  color={item.selected ? "primary" : ""}
                  sx={{ fontWeight: item.selected ? 600 : 400 }}
                >
                  {item.shortName}
                </Typography>
              ) : (
                <Typography
                  variant="body1"
                  color={item.selected ? "primary" : ""}
                  key={item.id}
                  sx={{
                    pl: "10px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  {item.name}
                </Typography>
              )}
            </Flex>
          </Link>
        ))}

        {/* Logout */}
        <Flex
          flexDirection="row"
          py="12px"
          px={collapsed ? "0px" : "32px"}
          justifyContent={collapsed ? "center" : "flex-start"}
          onClick={onLogoutClicked}
          sx={{ cursor: "pointer", "&:hover": { bgcolor: "rgb(226 232 240)" } }}
          title={collapsed ? "Logout" : undefined}
        >
          {collapsed ? (
            <Typography variant="subtitle1">L</Typography>
          ) : (
            <Typography variant="subtitle1" sx={{ pl: "10px" }}>
              Logout
            </Typography>
          )}
        </Flex>
      </Flex>
    </Box>
  );
};

export default LeftNav;
